import {makeAutoObservable} from "mobx";
import axios from "axios";
import {getStore, useStore} from './index'

/**
 * @typedef {object} NotificationInfo
 * @property {string} _id
 * @property {UserInfoReduce} sender
 * @property {UserInfoReduce} receiver
 * @property {string} page
 * @property {string} comment
 * @property {string} content
 * @property {boolean} isRead
 * @property {boolean} isConfirm
 * @property {string} expiresAt
 * @property {string} createdAt
 * @property {string} updatedAt
 */

export class NotificationStore {
    store;
    /** @type {NotificationInfo[]} */
    dataList = [];
    /** @type {{all:number,mention:number,confirm:number}} */
    count = {
        all: 0, mention: 0, confirm: 0
    };
    badge = "";
    /** @type {NotificationInfo[]} */
    popups = [];

    /** @type {string[]} */
    allUserNames = [];
    /** @type {{id:string,display:string}[]} */
    mentionUserNames = [];
    /** @type {string[]} */
    delUserNames = [];
    /** @type {number} */
    userNamesMutate = undefined;
    pageIndex = 1;
    pageLimit = 10;
    pageSort = "-createdAt";
    pageSearchSort = "content";
    pageLength = [
        {value: 10, label: "10개씩 보기"},
        {value: 30, label: "30개씩 보기"},
        {value: 50, label: "50개씩 보기"},
        {value: 100, label: "100개씩 보기"},
    ];
    listSort = [
        {value: "-createdAt", label: "최신 생성 순"},
        {value: "createdAt", label: "오래된 생성 순"},
    ];
    selectSort = [
        {value: "content", label: "내용"},
        {value: "sender", label: "이름"},
    ]
    pageQuery = `offset=${this.pageIndex}&limit=${this.pageLimit}&sort=${this.pageSort}`;

    constructor(store) {
        this.store = store;
        makeAutoObservable(this, {}, {autoBind: true});
    }


    /**
     * 최초 스토어를 세팅하는 함수
     * NotificationPopup 에 표현될 알림 리스트와 코멘트 작성에 필요한 userNameList 를 업데이트 해주는 함수
     */
    initStore() {
        this.pageIndex = 1;
        this.pageLimit = 10;
        this.pageSort = "-createdAt";
        this.pageSearchSort = "content";
        this.pageQuery = `offset=${this.pageIndex}&limit=${this.pageLimit}&sort=${this.pageSort}`;
        const token = localStorage.getItem('token');
        axios.get(`${import.meta.env.VITE_DEV_SERVER_URL}/notification?limit=100&sort=-createdAt`, {
            headers: {authorization: token, "Content-Type": "application/json"},
        }).then(async (response) => {
            await this.setPopups(response.data.data);
        });
        axios.get(`${import.meta.env.VITE_DEV_SERVER_URL}/users/nameList`, {
            headers: {authorization: token, "Content-Type": "application/json"},
        }).then(async (response) => {
            // console.log(response.data.data);
            this.setUserNameLists(response.data.data.userNames);
        });

    }

    /**
     * 한 페이지에 표현될 최대 개수를 세팅하는 함수
     * 기본값 10
     * @param {number} limit 최대 개수
     */
    setPageLimit(limit) {
        this.pageLimit = limit;
    }

    /**
     *  페이지 최신 오래된 생성 순
     * 기본값 "-createdAt"
     * @param {string} sort 최신 생성 기본값
     */
    setPageList(sort) {
        this.pageSort = sort;
    }

    /**
     * 페이지 검색시 검색 조건
     * 기본값 "content"
     * @param {string} searchSort 검색 조건 세팅값
     */
    setPageSearchList(searchSort) {
        this.pageSearchSort = searchSort;
    }


    /**
     * 페이지네이션에서 사용되는 인덱스 넘버를 세팅하는 함수
     * @param {number} index 인덱스 넘버
     */
    async setPageIndex(index) {
        this.pageIndex = index;
        await this.fetchNotificationPageList();
    }

    /**
     * 검색, 소팅 페이지 네이션 세팅 하는 함수
     * @param {string} badgeType 알림 현재 타입
     * @param {string} searchString 검색 내용
     */
    async fetchNotificationPageList(badgeType, searchString) {
        let query = `offset=${this.pageIndex}&limit=${this.pageLimit}&sort=${this.pageSort}`;
        if (badgeType === "confirm") {
            query += `&isConfirm=true`
        }
        if (searchString && searchString.length > 0) {
            query += `&searchType=${this.pageSearchSort}&searchString=${searchString}`;
        }
        this.pageQuery = query;
    }

    /**
     * 새로운 알림이 있을 경우 빨간색 배지가 뜨도록 해주는 함수
     * @param {string} status 상태값
     */
    setBadge(status) {
        this.badge = status;
    }

    /**
     * NotificationCenter 에서 검색조건에 맞는 리스트를 읽어와 리스트에 세팅해주는 함수
     * @param {NotificationInfo[]} dataList
     */
    setDataList(dataList) {
        this.dataList = dataList;
        this.readAll().then();
    }

    /**
     * 알림 리스트를 초기화해주는 함수
     */
    clearDataList() {
        this.dataList = [];
    }

    /**
     * 여러 개의 알림을 읽음처리하는 함수
     * @param {string[]} notificationIds 알림 아이디들
     * @return {Promise<void>}
     */
    async read(notificationIds) {
        await axios.patch(`/notification/read`, {notificationIds}, {headers: {skipInterceptor: true}});
    }

    /**
     * 전체 알림을 한번에 읽음처리하는 함수
     * @return {Promise<void>}
     */
    async readAll() {
        await axios.patch(`/notification/readAll`, {}, {headers: {skipInterceptor: true}});
    }

    /**
     * 알림의 항목별 개수 정보를 세팅하는 함수
     * @param {{all:number,mention:number,confirm:number}} count
     */
    setCount(count) {
        this.count = count;
    }

    /**
     * 알림팝업에 표현될 알림 리스트를 가져와서 세팅하는 함수
     * @return {Promise<void>}
     */
    async setPopups({notifications, count}) {
        try {
            this.popups = notifications;
            this.count = count;

            for (const notification of this.popups) {
                if (notification.isRead === false) {
                    this.badge = "active";
                    break;
                }
            }
        } catch (e) {
            console.log(e.message)
        }
    }

    /**
     * 알림팝업 기본 개수 너머의 알림들을 가져올 때 사용되는 함수 (ex: 무한 스크롤)
     * @param {NotificationInfo} data 알림 데이터
     * @return {void}
     */
    addPopup(data) {
        try {
            this.popups = [data, ...this.popups];
            this.setBadge("active");
        } catch (e) {
            console.log(e.message);
        }
    }

    /**
     * 코멘트에서 다른 유저들을 태깅하기 위한 리스트를 세팅하는 함수
     * @return {void}
     */
    setUserNameLists(userNames) {
        try {
            // const response = await axios.get('/users/nameList');
            const userNameData = userNames;
            /** @type {string[]} */
            const allUserNames = [];
            /** @type {{id:string,display:string}[]} */
            const mentionUserNames = [];
            /** @type {string[]} */
            const delUserNames = [];
            userNameData['ALIVE'].forEach((e) => {
                allUserNames.push(`(@/${e})`);
                mentionUserNames.push({id: e, display: `@${e}`});
            });
            userNameData['DELETED'].forEach((e) => {
                delUserNames.push(`(@/${e})`);
            });
            this.allUserNames = allUserNames;
            this.mentionUserNames = mentionUserNames;
            this.delUserNames = delUserNames;
        } catch (e) {
            console.log(e.message);
        }
    }

    /**
     * 알림을 확인 완료 처리해주는 함수
     * @param {NotificationInfo} item
     * @return {Promise<void>}
     */
    async confirm(item) {
        if (!item) return;
        let moreData = {
            notificationId: item._id
        }
        await axios.patch(`/notification/confirm`, moreData, {headers: {skipInterceptor: true}});
        for (const notification of this.dataList) {
            if (notification._id === item._id) {
                notification.isConfirm = true;
                break;
            }
        }
        for (const notification of this.popups) {
            if (notification._id === item._id) {
                notification.isConfirm = true;
                break;
            }
        }

    }

    /**
     * 컨텐츠에 존재하는 userName 을 찾아 태그 형식으로 변경해주는 함수
     * @param {NotificationInfo|CommentInfo} item
     * @return {string}
     */
    replaceTag(item) {
        /** @type {string} */
        let tagContent = item.content;
        const filterNameList = this.allUserNames.filter((x) => tagContent.indexOf(x) > -1);
        const filterNameDelList = this.delUserNames.filter((x) => tagContent.indexOf(x) > -1);
        filterNameList.forEach((itm) => {
            const inTextName = itm.replace(/[@()/]/gim, '');
            tagContent = tagContent.replaceAll(itm, `<span class="link">@${inTextName}</span>`);
        });
        filterNameDelList.forEach((itm) => {
            const inTextName = itm.replace(/[@()/]/gim, '');
            tagContent = tagContent.replaceAll(itm, `<span class="un_link">@${inTextName}</span>`);
        });
        return tagContent;
    }
}

export const useNotificationStore = () => {
    return useStore().notificationStore;
}

export const getNotificationStore = () => {
    return getStore().notificationStore;
}